<template>
  <div class="managerial-test">
    <div class="test-header">
      <h2>{{ test.name }}</h2>
      <div class="instructions" v-html="formattedInstructions"></div>
      <div class="progress-info">
        <div class="question-progress">
          Question {{ currentQuestionIndex + 1 }} sur {{ test.questions.length }}
        </div>
        <div class="time-remaining" v-if="timeRemaining">
          Temps restant : {{ formatTime(timeRemaining) }}
        </div>
      </div>
    </div>

    <div class="question-container" v-if="currentQuestion">
      <div class="question-text">{{ currentQuestion.text }}</div>
      <div class="paired-choices">
        <div 
          v-for="answer in currentQuestion.answers" 
          :key="answer.id"
          class="choice-option"
          :class="{ selected: selectedAnswer === answer.id }"
          @click="selectAnswer(answer.id)">
          <div class="radio-button">
            <div class="inner-circle" v-if="selectedAnswer === answer.id"></div>
          </div>
          <div class="answer-text">{{ answer.text }}</div>
        </div>
      </div>
    </div>

    <div class="navigation-buttons">
      <button 
        class="btn btn-secondary" 
        @click="previousQuestion"
        :disabled="currentQuestionIndex === 0">
        <i class="fas fa-arrow-left"></i> Précédent
      </button>
      <button 
        class="btn btn-primary" 
        @click="nextQuestion"
        :disabled="!selectedAnswer">
        {{ isLastQuestion ? 'Terminer' : 'Suivant' }}
        <i :class="isLastQuestion ? 'fas fa-check' : 'fas fa-arrow-right'"></i>
      </button>
    </div>
  </div>
</template>

<script>

export default {
  name: 'TestManagerialPotential',
  
  props: {
    test: {
      type: Object,
      required: true
    },
    assignedTestId: {
      type: [String, Number],
      required: true
    },
    initialProgress: {
      type: Object,
      default: () => ({ currentQuestion: 0, answers: {} })
    },
    timeLimit: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      currentQuestionIndex: this.initialProgress.currentQuestion || 0,
      userAnswers: this.initialProgress.answers || {},
      selectedAnswer: null,
      timeRemaining: this.timeLimit * 60,
      timer: null
    }
  },

  computed: {
    currentQuestion() {
      return this.test.questions[this.currentQuestionIndex]
    },

    isLastQuestion() {
      return this.currentQuestionIndex === this.test.questions.length - 1
    },

    formattedInstructions() {
      return this.test.instructions.replace(/\n/g, '<br>')
    }
  },

  methods: {
    async saveProgress() {
      if (!this.assignedTestId) {
        console.error('ID du test assigné manquant')
        return
      }

      try {
        await this.$http.post(`/tests/${this.assignedTestId}/save-progress`, {
          current_question: this.currentQuestionIndex,
          answers: this.userAnswers
        })
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },

    async selectAnswer(answerId) {
      this.selectedAnswer = answerId
      this.userAnswers[this.currentQuestion.id] = answerId
      
      try {
        await this.saveProgress()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la progression:', error)
      }
    },

    async nextQuestion() {
      if (this.isLastQuestion) {
        await this.submitTest()
      } else {
        this.currentQuestionIndex++
        this.selectedAnswer = this.userAnswers[this.currentQuestion.id] || null
      }
    },

    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--
        this.selectedAnswer = this.userAnswers[this.currentQuestion.id] || null
      }
    },

    async submitTest() {
      if (!this.assignedTestId) {
        console.error('ID du test assigné manquant')
        return
      }

      try {
        const formattedAnswers = Object.entries(this.userAnswers).map(([questionId, answerId]) => ({
          question_id: parseInt(questionId),
          answer_id: parseInt(answerId)
        }))
        
        const response = await this.$http.post(`/tests/${this.assignedTestId}/submit`, { 
          answers: formattedAnswers
        })

        if (response.data.job_application_id) {
          this.$emit('completed', response.data.job_application_id)
        } else {
          throw new Error('ID de candidature non trouvé dans la réponse')
        }
      } catch (error) {
        console.error('Error submitting test:', error)
        throw new Error('Erreur lors de la soumission du test')
      }
    },

    startTimer() {
      if (this.timeLimit > 0) {
        this.timer = setInterval(() => {
          if (this.timeRemaining > 0) {
            this.timeRemaining--
          } else {
            clearInterval(this.timer)
            this.submitTest()
          }
        }, 1000)
      }
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    }
  },

  mounted() {
    this.startTimer()
    if (this.currentQuestion) {
      this.selectedAnswer = this.userAnswers[this.currentQuestion.id] || null
    }
  },

  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }
}
</script>

<style scoped>
.managerial-test {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.test-header {
  margin-bottom: 30px;
}

.instructions {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  line-height: 1.6;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
  padding: 10px;
  background-color: #e9ecef;
  border-radius: 8px;
}

.question-progress {
  font-weight: bold;
  color: #495057;
}

.time-remaining {
  font-weight: bold;
  color: #2596be;
}

.question-text {
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #2c3e50;
}

.paired-choices {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 30px 0;
}

.choice-option {
  display: flex;
  align-items: center;
  padding: 15px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.choice-option:hover {
  border-color: #2596be;
  background-color: #f8f9fa;
}

.choice-option.selected {
  border-color: #2596be;
  background-color: #e6f3ff;
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid #2596be;
  border-radius: 50%;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inner-circle {
  width: 10px;
  height: 10px;
  background-color: #2596be;
  border-radius: 50%;
}

.answer-text {
  flex: 1;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-weight: 500;
}

.btn i {
  font-size: 0.9em;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>